import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { RouterModule } from '@angular/router';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import { Brand } from '@principle-theorem/principle-core';
import {
  type IPractice,
  type ITemplateDefinition,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  filterUndefined,
  isSameRef,
  multiFilter,
  undeletedFilter,
  where,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { isUndefined, omitBy } from 'lodash';
import { type Observable } from 'rxjs';
import { debounceTime, switchMap, take } from 'rxjs/operators';

interface IReferralTemplateSlot {
  templateRef: DocumentReference<ITemplateDefinition> | undefined;
  subject: string;
}

interface IReferralEmailDefaultsForm {
  initialSend: IReferralTemplateSlot;
  updateNotification: IReferralTemplateSlot;
  patientLetter: IReferralTemplateSlot;
}

@Component({
  selector: 'pr-referral-email-defaults-practice',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    RouterModule,
  ],
  templateUrl: './referral-email-defaults-practice.component.html',
  styleUrl: './referral-email-defaults-practice.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralEmailDefaultsPracticeComponent {
  private _organisation = inject(OrganisationService);

  readonly practice = input.required<WithRef<IPractice>>();

  referralTemplates$: Observable<WithRef<ITemplateDefinition>[]>;

  readonly subjectVariablesHint =
    'Supports variables: {{ patientFirstName }}, {{ patientName }}, {{ practiceName }}, {{ recipientName }}';
  readonly initialSendSubjectPlaceholder = 'Referral for {{ patientName }}';
  readonly updateNotificationSubjectPlaceholder =
    'Referral Update for {{ patientName }}';
  readonly patientLetterSubjectPlaceholder = 'Your Referral Letter';

  form = new TypedFormGroup<IReferralEmailDefaultsForm>({
    initialSend: new TypedFormGroup<IReferralTemplateSlot>({
      templateRef: new TypedFormControl<
        DocumentReference<ITemplateDefinition> | undefined
      >(undefined),
      subject: new TypedFormControl<string>(''),
    }),
    updateNotification: new TypedFormGroup<IReferralTemplateSlot>({
      templateRef: new TypedFormControl<
        DocumentReference<ITemplateDefinition> | undefined
      >(undefined),
      subject: new TypedFormControl<string>(''),
    }),
    patientLetter: new TypedFormGroup<IReferralTemplateSlot>({
      templateRef: new TypedFormControl<
        DocumentReference<ITemplateDefinition> | undefined
      >(undefined),
      subject: new TypedFormControl<string>(''),
    }),
  });

  constructor() {
    toObservable(this.practice)
      .pipe(
        switchMap((practice) => Firestore.doc$(practice.ref).pipe(take(1))),
        takeUntilDestroyed()
      )
      .subscribe((practice) => {
        const defaults = practice?.settings.referral?.emailTemplateDefaults;
        this.form.patchValue(
          {
            initialSend: {
              templateRef: defaults?.initialSend?.templateRef,
              subject: defaults?.initialSend?.subject ?? '',
            },
            updateNotification: {
              templateRef: defaults?.updateNotification?.templateRef,
              subject: defaults?.updateNotification?.subject ?? '',
            },
            patientLetter: {
              templateRef: defaults?.patientLetter?.templateRef,
              subject: defaults?.patientLetter?.subject ?? '',
            },
          },
          { emitEvent: false }
        );
      });

    this.referralTemplates$ = this._organisation.brand$.pipe(
      filterUndefined(),
      switchMap((brand) =>
        Firestore.query$(
          Brand.templateCol(brand),
          where('scope', '==', TemplateScope.Referral)
        )
      ),
      multiFilter(undeletedFilter)
    );

    this.form.valueChanges
      .pipe(debounceTime(500), takeUntilDestroyed())
      .subscribe(() => {
        void this.saveSettings();
      });
  }

  compareTemplateRef = (
    current: DocumentReference<ITemplateDefinition> | undefined,
    option: DocumentReference<ITemplateDefinition> | undefined
  ): boolean => {
    if (!current || !option) {
      return current === option;
    }
    return isSameRef(current, option);
  };

  async saveSettings(): Promise<void> {
    const practice = this.practice();
    const { initialSend, updateNotification, patientLetter } =
      this.form.getRawValue();

    const buildSlot = (slot: IReferralTemplateSlot): Record<string, unknown> =>
      omitBy(
        { templateRef: slot.templateRef, subject: slot.subject || undefined },
        isUndefined
      );

    const update: Record<string, unknown> = {
      'settings.referral.emailTemplateDefaults': {
        initialSend: buildSlot(initialSend),
        updateNotification: buildSlot(updateNotification),
        patientLetter: buildSlot(patientLetter),
      },
    };

    await Firestore.updateDoc(practice.ref, update);
  }
}
